import type React from 'react';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useInterval } from 'usehooks-ts';

import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import MainLayout from '../../layout/MainLayout/MainLayout';
import AppointmentSummary from './AppointmentSummary';
import ChatHeader from './ChatHeader';
import ConversationSection from './ConversationSection';
import MenuChatsList from './MenuChatsList';
import MenuHeader from './MenuHeader';
import SendMessagePanel from './SendMessagePanel';

import { isEqual, isNil } from 'lodash-es';
import { ROUTES } from '../../../constants/Constants';
import { lazyReactNaiveRetry } from '../../../constants/Lazy';
import useAppNavigation from '../../../hooks/useAppNavigation';
import usePushNotifications from '../../../hooks/usePushNotifications';
import useUser from '../../../hooks/useUser';
import { ChatMessageType } from '../../../proxies/ChatProxy';
import {
  getConversationNewMessagesRequest,
  getRecentConversationsRequest,
  setCurrentConversation,
  setMessageWithActionTimestamp,
  setMessages,
} from '../../../redux/ducks/ChatDucks';
import type {
  ChatMessageDataType,
  ConversationMessageDataTimelineType,
  ConversationMessageDataType,
  ConversationUserType,
  GetConversationMessagesResponseDataType,
} from '../../../redux/ducks/ChatDucks.types';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import MainPopupMenu from '../../layout/Navbar/MainPopupMenu';
import Spinner from '../../layout/Spinner/Spinner';

const ChatUserDetailsModal = lazyReactNaiveRetry(
  () => import('../../modals/ChatUserProfileModal'),
);

const ChatModifyAppointmentModal = lazyReactNaiveRetry(
  () => import('../../modals/ChatModifyAppointmentModal'),
);

// https://github.com/demon-bixia/Avian-Template

const NEW_CONVERSATION_MESSAGES_LONG_POLLING_DELAY = 5000;

const PUSH_MESSAGE_TEMPLATE: ChatMessageDataType = {
  data: {
    id: '37a0fe33-5c96-484b-99c4-f9ccc2d8cc57',
    type: ChatMessageType.PushEnableTip,
    timestamp: 0,
  },
  mine: false,
  system: true,
  partner: '',
  provider: '',
  sending: false,
  user: undefined,
  disabled: false,
};

interface MessagesProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

const Messages: React.FC<MessagesProps> = ({ ssrFetchedData }) => {
  const { conversationId } = useParams();
  const { navigate, toLocalizedRoute } = useAppNavigation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isSupported: isPushSupported, isPermitted: isPushPermitted } =
    usePushNotifications();
  const { currentUser } = useUser();
  const scrollableArea = useRef<any>(null);
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [forceNewMessages, setForceNewMessages] = useState<boolean>(false);
  const [newMessagesToProcess, setNewMessagesToProcess] =
    useState<GetConversationMessagesResponseDataType | null>();
  const [conversationReceived, setConversationsReceived] =
    useState<boolean>(false);
  const [conversationUpdateRequired, setConversationUpdateRequired] =
    useState<boolean>(false);
  const {
    currentConversation,
    getRecentConversationsResponse,
    getConversationMessagesResponse,
    getConversationNewMessagesResponse,
    messages: receivedMessages,
    messageWithActionTimestamp,
  } = useSelector((state: RootReducerType) => state.chat);

  const { onUpdateAppointment } = useSelector(
    (state: RootReducerType) => state.appointment,
  );

  const { pushPreference } = useSelector(
    (state: RootReducerType) => state.storage,
  );

  const onChatClick = useCallback(
    (cId: string) => {
      navigate(
        toLocalizedRoute(
          ROUTES.CONVERSATION_ROUTE.replace(':conversationId', cId),
        ),
      );
    },
    [navigate],
  );

  useEffect(() => {
    if (conversationId) {
      dispatch(setCurrentConversation(conversationId));
      setConversationUpdateRequired(true);

      if (!chatOpen) {
        setChatOpen(true);
      }
    }
  }, [conversationId]);

  const getNewMessages = useCallback(() => {
    if (currentConversation) {
      dispatch(
        getConversationNewMessagesRequest({
          conversationId: currentConversation,
          update: chatOpen,
          after: !isNil(messageWithActionTimestamp)
            ? messageWithActionTimestamp - 1000
            : !isNil(receivedMessages) && receivedMessages.messages.length > 0
              ? receivedMessages.messages[0].data.timestamp
              : new Date().getTime(),
        }),
      );
      if (!isNil(messageWithActionTimestamp)) {
        dispatch(setMessageWithActionTimestamp(null));
      }
    }
  }, [
    currentConversation,
    receivedMessages,
    chatOpen,
    messageWithActionTimestamp,
    dispatch,
  ]);

  useEffect(() => {
    if (getRecentConversationsResponse?.data && !conversationReceived) {
      setConversationsReceived(true);
      if (conversationId && !currentConversation) {
        navigate(
          toLocalizedRoute(
            ROUTES.CONVERSATION_ROUTE.replace(
              ':conversationId',
              conversationId,
            ),
          ),
          {
            replace: true,
          },
        );
      }
    }
  }, [getRecentConversationsResponse]);

  useEffect(() => {
    if (
      currentUser &&
      getConversationNewMessagesResponse?.data &&
      conversationUpdateRequired === true &&
      chatOpen === true
    ) {
      dispatch(getRecentConversationsRequest({}));
      setConversationUpdateRequired(false);
    }
  }, [getConversationNewMessagesResponse]);

  useEffect(() => {
    if (currentUser) {
      dispatch(getRecentConversationsRequest({}));
    }
  }, [currentUser]);

  useEffect(
    () => () => {
      dispatch(setCurrentConversation(null));
      setChatOpen(false);
      setNewMessagesToProcess(null);
      setConversationsReceived(false);
      setConversationUpdateRequired(false);
    },
    [],
  );

  useInterval(getNewMessages, NEW_CONVERSATION_MESSAGES_LONG_POLLING_DELAY);

  useEffect(() => {
    if (
      getConversationMessagesResponse?.data &&
      !isEqual(newMessagesToProcess, getConversationMessagesResponse?.data)
    ) {
      setNewMessagesToProcess(getConversationMessagesResponse?.data);
    }
  }, [getConversationMessagesResponse]);

  useEffect(() => {
    if (
      getConversationNewMessagesResponse?.data &&
      !isEqual(newMessagesToProcess, getConversationNewMessagesResponse?.data)
    ) {
      setNewMessagesToProcess(getConversationNewMessagesResponse?.data);
    }
  }, [getConversationNewMessagesResponse]);

  // Update current conversation messages
  useEffect(() => {
    if (!currentConversation || !newMessagesToProcess) {
      return;
    }

    let convUsers: ConversationUserType[] = [];
    let partner: string | undefined;
    let provider: string | undefined;
    let timeline: ConversationMessageDataTimelineType | undefined;
    let statusMessage: ConversationMessageDataType | undefined;

    let validMessages = false;
    if (newMessagesToProcess.conversation === currentConversation) {
      validMessages = true;
    }

    if (validMessages && newMessagesToProcess.messages.length > 0) {
      const now = Date.now();
      const convMessages: ChatMessageDataType[] = [];
      convUsers = newMessagesToProcess.users;
      partner = newMessagesToProcess.partner;
      provider = newMessagesToProcess.provider;
      timeline = newMessagesToProcess.timeline;
      statusMessage = newMessagesToProcess?.status_message;
      let statusChatMessage: ChatMessageDataType | undefined;
      if (statusMessage) {
        statusChatMessage = {
          data: statusMessage,
          mine:
            !isNil(statusMessage?.user_id) &&
            !isNil(currentUser?.id) &&
            statusMessage?.user_id === currentUser?.id,
          partner,
          provider,
          sending: false,
          user: convUsers && convUsers.length > 0 ? convUsers[0] : undefined,
          disabled: false,
        };
      }

      for (
        let index = 0;
        index < newMessagesToProcess.messages.length;
        index++
      ) {
        const receivedMessage = newMessagesToProcess.messages[index];
        const myMessage =
          !isNil(receivedMessage?.user_id) &&
          !isNil(currentUser?.id) &&
          receivedMessage?.user_id === currentUser?.id;

        const chatMsg: ChatMessageDataType = {
          data: receivedMessage,
          mine: myMessage,
          partner,
          provider,
          sending: false,
          user: convUsers && convUsers.length > 0 ? convUsers[0] : undefined,
          disabled: false,
        };

        convMessages.push(chatMsg);
      }

      const inputMessages: ChatMessageDataType[] = receivedMessages
        ? [...convMessages, ...receivedMessages.messages]
        : [...convMessages];

      let newMessages: ChatMessageDataType[] = inputMessages
        .reduce(
          (
            unique: ChatMessageDataType[],
            item: ChatMessageDataType,
          ): ChatMessageDataType[] => {
            const existingUnique: ChatMessageDataType[] = unique.filter(
              (x) =>
                x.data.id !== item.data.id &&
                (isNil(x.data?.reference_id) ||
                  isNil(item.data?.reference_id) ||
                  x.data?.reference_id !== item.data?.reference_id),
            );

            if (existingUnique.length === unique.length) {
              return [...unique, item];
            }

            const existingItem = unique.filter(
              (x) =>
                x.data.id === item.data.id ||
                (!isNil(x.data?.reference_id) &&
                  !isNil(item.data?.reference_id) &&
                  x.data?.reference_id === item.data?.reference_id),
            );

            const newElement: ChatMessageDataType = item.sending
              ? { ...existingItem[0], first: false }
              : { ...item, first: false };

            return [...existingUnique, newElement];
          },
          [],
        )
        .sort((a, b) => b.data.timestamp - a.data.timestamp);

      if (newMessages.length > 0) {
        newMessages = newMessages.map((a, index) => ({
          ...a,
          first: index === newMessages.length - 1,
        }));
      }

      if (
        isNil(pushPreference) &&
        isPushSupported &&
        !isPushPermitted &&
        newMessages.length > 0 &&
        newMessages[0].mine
      ) {
        if (
          newMessages.filter((m) => m.data.id === PUSH_MESSAGE_TEMPLATE.data.id)
            .length === 0
        ) {
          const pushMessage: ChatMessageDataType = {
            ...PUSH_MESSAGE_TEMPLATE,
            data: { ...PUSH_MESSAGE_TEMPLATE.data, timestamp: now },
            user: convUsers && convUsers.length > 0 ? convUsers[0] : undefined,
          };

          newMessages = [pushMessage, ...newMessages];
        }
      }

      const processedMessages = newMessages.reduce(
        (reduced: ChatMessageDataType[], toProcess: ChatMessageDataType) => [
          ...reduced,
          {
            ...toProcess,
            disabled:
              isNil(statusMessage) ||
              statusMessage.id !== toProcess.data.id ||
              (!isNil(toProcess.data?.meta) &&
                toProcess.data.meta.current_schedule <= now &&
                (isNil(toProcess.data?.meta.change_request_schedule) ||
                  toProcess.data.meta.change_request_schedule <= now)),
          },
        ],
        [],
      );

      const sortedMessages: ChatMessageDataType[] = [];
      for (let fidx = 0; fidx < processedMessages.length; fidx++) {
        const el = { ...processedMessages[fidx] };
        if (fidx > 0) {
          el.next = processedMessages[fidx - 1];
        }
        if (fidx < processedMessages.length - 2) {
          el.previous = processedMessages[fidx + 1];
        }
        sortedMessages.push(el);
      }

      if (timeline) {
        const newMgs = {
          total: timeline.total_messages,
          messages: sortedMessages,
        };

        if (!isEqual(receivedMessages, newMgs)) {
          dispatch(
            setMessages({
              conversation: currentConversation,
              total: timeline.total_messages,
              status: statusChatMessage, // TODO
              messages: sortedMessages,
            }),
          );
        }
      }
    }
  }, [newMessagesToProcess]);

  useEffect(() => {
    if (onUpdateAppointment && !forceNewMessages) {
      setForceNewMessages(true);
    } else if (!onUpdateAppointment && forceNewMessages) {
      setForceNewMessages(false);
      // trigger chat update if open
      if (chatOpen) {
        getNewMessages();
      }
    }
  }, [onUpdateAppointment]);

  return (
    <MainLayout
      categories={false}
      search={false}
      navbar={false}
      footer={false}
      ssrFetchedData={ssrFetchedData}
    >
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <ChatUserDetailsModal />
      </Suspense>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <ChatModifyAppointmentModal />
      </Suspense>
      <div className="flex size-full grow flex-col">
        {conversationReceived ? (
          <span className="grid size-full min-h-full grow grid-cols-4">
            <span
              className={`${
                chatOpen ? 'hidden' : 'col-span-4 flex'
              } min-h-full flex-col items-center justify-start border-r border-solid border-slate-300 lg:col-span-1 lg:flex`}
            >
              <span className="min-w-full">
                <MenuHeader />
              </span>
              <span className="flex min-w-full grow flex-col">
                <MenuChatsList onChatClick={onChatClick} />
              </span>
            </span>
            <span
              className={`${
                chatOpen ? 'col-span-4 flex' : 'hidden'
              } flex-col items-center justify-start lg:col-span-3 lg:flex`}
            >
              {currentConversation ? (
                <>
                  <span className="min-w-full">
                    <ChatHeader
                      onBack={() => {
                        setChatOpen(false);
                        dispatch(setCurrentConversation(null));
                        navigate(-1);
                      }}
                    />
                  </span>
                  <span className="min-w-full grow">
                    <span className="relative flex min-h-full flex-col items-start justify-start">
                      <span className="relative flex min-w-full grow flex-col items-end justify-end">
                        <ConversationSection
                          scrollableRef={scrollableArea}
                          chatOpen={chatOpen}
                        />
                      </span>
                      <span className="absolute top-0 min-w-full">
                        <AppointmentSummary />
                      </span>
                      <span className="mb-1 min-w-full py-1">
                        <SendMessagePanel
                          id="send-chat-message-panel"
                          scrollableRef={scrollableArea}
                        />
                      </span>
                    </span>
                  </span>
                </>
              ) : (
                <div className="relative flex size-full flex-col items-center justify-center text-center text-sm font-semibold">
                  <span className="flex flex-col items-center justify-center gap-3 text-center text-slate-500">
                    {t('static.chat.no_conversation_selected_message')}
                  </span>
                  <span className="absolute right-0 top-0 flex h-[64px] max-h-[64px] min-w-full flex-row items-center justify-end border-b border-slate-300 p-2">
                    <span>
                      <MainPopupMenu />
                    </span>
                  </span>
                </div>
              )}
            </span>
          </span>
        ) : (
          <div className="flex min-h-[calc(100dvh)] w-screen flex-col items-center justify-center">
            <Spinner color="fill-sky-600" />
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Messages;
